import React from 'react'
import 'bootstrap/dist/css/bootstrap.min.css'
import Parse from 'parse'
import Config from '../config'
import {
  wilsonCI,
  proportionZTest,
  significanceLabel,
  sampleRatioMismatch,
  welchTTest,
  meanCI,
  relativeLiftCI,
  meanLiftCI,
  readVerdict,
  minimumDetectableEffect,
  adjustedAlpha
} from './experimentStats'

/* global Chart */

// --- Formatting helpers ---

/**
 * Format a rate and its confidence interval as percentages.
 */
function fmtPctCI (rate, ci) {
  const pct = (rate * 100).toFixed(1)
  const lo = (ci[0] * 100).toFixed(1)
  const hi = (ci[1] * 100).toFixed(1)
  return { pct, lo, hi }
}

// Field defaults for a results payload. The console and the backend deploy
// separately, so the console can briefly be talking to an older server; a
// missing numeric field would otherwise reach toFixed/toLocaleString and take
// the whole results page down rather than degrading one cell.
const VARIANT_METRIC_DEFAULTS = {
  userCount: 0,
  eligibleUsers: 0,
  everSubscribed: 0,
  startedTrial: 0,
  resolvedTrials: 0,
  trialConverted: 0,
  directPaid: 0,
  activeSubscriptions: 0,
  payingUsers: 0,
  preExistingSubscribers: 0,
  knownSubscriptionState: 0,
  totalRevenue: 0,
  revenuePerUser: 0,
  revenuePerPayingUser: 0,
  revenueSumOfSquares: 0,
  preExistingRevenue: 0,
  medianExposureDays: 0,
  usersWithUnresolvedTrialWindow: 0
}

const PRODUCT_MIX_DEFAULTS = {
  yearly: 0,
  quarterly: 0,
  monthly: 0,
  lifetime: 0,
  unclassified: 0
}

function normalizeVariantMetrics (metrics) {
  const normalized = { ...VARIANT_METRIC_DEFAULTS, ...metrics }
  for (const key of Object.keys(VARIANT_METRIC_DEFAULTS)) {
    if (typeof normalized[key] !== 'number' || !isFinite(normalized[key])) {
      normalized[key] = VARIANT_METRIC_DEFAULTS[key]
    }
  }
  normalized.productMix = { ...PRODUCT_MIX_DEFAULTS, ...(metrics.productMix || {}) }
  // An older payload has no eligibleUsers; every conversion rate divides by it,
  // so fall back to the assigned count rather than to zero.
  if (!metrics || typeof metrics.eligibleUsers !== 'number') {
    normalized.eligibleUsers = normalized.userCount - normalized.preExistingSubscribers
  }
  return normalized
}

export function normalizeResults (results) {
  if (!results || !results.variants) return results
  const variants = {}
  for (const [key, metrics] of Object.entries(results.variants)) {
    variants[key] = normalizeVariantMetrics(metrics || {})
  }
  return {
    ...results,
    variants,
    allocation: results.allocation || [],
    dailyData: results.dailyData || [],
    pendingBackfill: results.pendingBackfill || 0,
    eventHistoryFailures: results.eventHistoryFailures || 0
  }
}

function fmtP (pValue) {
  return pValue < 0.001 ? '<0.001' : pValue.toFixed(3)
}

/**
 * A lift, always signed. An unsigned "11.1%" next to a rate reads as a rate.
 */
function fmtSignedPct (pct) {
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`
}

// Lift is only coloured once it clears the threshold the page is reading at.
// A green "+18%" on a result indistinguishable from noise is the single
// easiest way to talk yourself into shipping a losing variant.
const TONE_COLORS = {
  positive: '#198754',
  negative: '#dc3545',
  muted: '#6c757d'
}

// --- Presentation constants ---

// One palette for the whole screen. The traffic-split bar, the comparison table
// headers and the charts all look a variant up by key, so a variant keeps the
// same colour wherever it appears on the page.
const VARIANT_COLORS = [
  '#0d6efd',
  '#198754',
  '#dc3545',
  '#fd7e14',
  '#6f42c1',
  '#20c997'
]

function variantColor (index) {
  return VARIANT_COLORS[index % VARIANT_COLORS.length]
}

const STATUS_META = {
  draft: { label: 'Draft', badgeClass: 'text-bg-secondary' },
  running: { label: 'Running', badgeClass: 'text-bg-success' },
  paused: { label: 'Paused', badgeClass: 'text-bg-warning' },
  ended: { label: 'Ended', badgeClass: 'text-bg-dark' }
}

const PLATFORM_LABELS = {
  ios: 'iOS',
  android: 'Android',
  all: 'All platforms'
}

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
})

function formatCurrency (value) {
  return currencyFormat.format(Number(value) || 0)
}

/**
 * Parse a value that may be a Parse date pointer ({ iso }) or a date string.
 */
function toDate (value) {
  if (!value) return null
  return new Date(value.iso || value)
}

function formatDate (value) {
  const date = toDate(value)
  return date ? date.toLocaleString() : null
}

export default class ExperimentDetailComponent extends React.Component {
  constructor (props) {
    super(props)
    const id = this.props.match.params.id
    if (id && id !== 'new') {
      this.state = {
        experiment: null,
        isEditing: false,
        assignmentCount: 0,
        results: null,
        loadingResults: false,
        chartInstances: []
      }
      this.loadContent(id)
    } else {
      const experiment = new Parse.Object('Experiment')
      experiment.set('status', 'draft')
      experiment.set('platform', 'ios')
      experiment.set('variants', [
        { key: 'control', weight: 50 },
        { key: 'variant_a', weight: 50 }
      ])
      this.state = {
        experiment,
        isEditing: true,
        assignmentCount: 0,
        results: null,
        loadingResults: false,
        chartInstances: []
      }
    }
  }

  componentWillUnmount () {
    this.destroyCharts()
  }

  destroyCharts = () => {
    if (this.state.chartInstances) {
      this.state.chartInstances.forEach(c => c.destroy())
    }
  }

  loadContent = async (id) => {
    try {
      const sessionToken = Parse.User.current()?.getSessionToken()
      if (!sessionToken) {
        window.alert('Not authenticated')
        return
      }

      const baseUrl = Config.serverUrl.replace('/parse/', '')
      const response = await fetch(`${baseUrl}/api/experiments/${id}`, {
        headers: { 'x-parse-session-token': sessionToken }
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to load experiment')
      }

      const data = await response.json()
      const experiment = new Parse.Object('Experiment')
      experiment.id = data.id
      experiment.set('key', data.key)
      experiment.set('name', data.name)
      experiment.set('description', data.description)
      experiment.set('status', data.status)
      experiment.set('platform', data.platform)
      experiment.set('variants', data.variants)
      experiment.set('startDate', data.startDate)
      experiment.set('endDate', data.endDate)
      this.setState({
        experiment,
        assignmentCount: data.assignmentCount || 0
      })
    } catch (error) {
      window.alert('Error loading experiment: ' + error.message)
    }
  }

  handleSave = async (event) => {
    event.preventDefault()
    try {
      const sessionToken = Parse.User.current()?.getSessionToken()
      if (!sessionToken) {
        window.alert('Not authenticated')
        return
      }

      const exp = this.state.experiment
      const baseUrl = Config.serverUrl.replace('/parse/', '')
      const isNew = !exp.id
      const status = exp.get('status')

      // An ended experiment is terminal — no new users are being assigned, so editing
      // it has no effect. The Edit button is hidden for ended experiments; block saves
      // here too in case the form is reached another way.
      if (!isNew && status === 'ended') {
        window.alert('This experiment has ended and can no longer be edited.')
        return
      }

      // Editing a started (running/paused) experiment does not re-bucket users who are
      // already assigned (assignments are sticky). It only changes the split for users
      // assigned from now on, which can skew results.
      if (!isNew && Boolean(status) && status !== 'draft') {
        const proceed = window.confirm(
          'This experiment has already started.\n\n' +
          'Saving will not change users already assigned — they keep their ' +
          'current variant. Your changes only affect users assigned from now ' +
          'on, which can skew results by making the variant mix depend on when ' +
          'users joined.\n\nContinue?'
        )
        if (!proceed) {
          return
        }
      }

      const url = isNew
        ? `${baseUrl}/api/experiments`
        : `${baseUrl}/api/experiments/${exp.id}`

      const body = {
        key: exp.get('key'),
        name: exp.get('name'),
        description: exp.get('description'),
        platform: exp.get('platform'),
        variants: exp.get('variants')
      }

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-parse-session-token': sessionToken
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Request failed')
      }

      const result = await response.json()
      window.location.href = '/#/experiments/' + result.id
      window.location.reload()
    } catch (error) {
      window.alert('Error saving: ' + error.message)
    }
  }

  handleFieldChange = (field, value) => {
    this.state.experiment.set(field, value)
    this.setState({ experiment: this.state.experiment })
  }

  handleVariantChange = (index, field, value) => {
    const variants = this.state.experiment.get('variants') || []
    variants[index] = { ...variants[index], [field]: field === 'weight' ? parseInt(value) || 0 : value }
    this.state.experiment.set('variants', variants)
    this.setState({ experiment: this.state.experiment })
  }

  handleAddVariant = (event) => {
    event.preventDefault()
    const variants = this.state.experiment.get('variants') || []
    variants.push({ key: '', weight: 0 })
    this.state.experiment.set('variants', variants)
    this.setState({ experiment: this.state.experiment })
  }

  handleRemoveVariant = (index) => {
    const variants = this.state.experiment.get('variants') || []
    variants.splice(index, 1)
    this.state.experiment.set('variants', variants)
    this.setState({ experiment: this.state.experiment })
  }

  handleLifecycleAction = async (action) => {
    const experiment = this.state.experiment
    if (!experiment || !experiment.id) return

    const sessionToken = Parse.User.current()?.getSessionToken()
    if (!sessionToken) {
      window.alert('Not authenticated')
      return
    }

    const baseUrl = Config.serverUrl.replace('/parse/', '')
    const url = `${baseUrl}/api/experiments/${experiment.id}/${action}`

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-parse-session-token': sessionToken
        }
      })
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Request failed')
      }
      window.location.reload()
    } catch (error) {
      window.alert('Error: ' + error.message)
    }
  }

  handleRefreshResults = async () => {
    const experiment = this.state.experiment
    if (!experiment || !experiment.id) return

    const sessionToken = Parse.User.current()?.getSessionToken()
    if (!sessionToken) {
      window.alert('Not authenticated')
      return
    }

    this.setState({ loadingResults: true })
    const baseUrl = Config.serverUrl.replace('/parse/', '')
    const url = `${baseUrl}/api/experiments/${experiment.id}/results`

    try {
      const response = await fetch(url, {
        headers: {
          'x-parse-session-token': sessionToken
        }
      })
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Request failed')
      }
      const results = normalizeResults(await response.json())
      this.setState({ results, loadingResults: false }, () => {
        this.renderCharts(results)
      })
    } catch (error) {
      window.alert('Error loading results: ' + error.message)
      this.setState({ loadingResults: false })
    }
  }

  handleStartEditing = () => {
    // The chart canvases unmount while the form is up, so drop the Chart.js
    // instances with them and rebuild against fresh canvases on cancel.
    this.destroyCharts()
    this.setState({ isEditing: true, chartInstances: [] })
  }

  handleCancelEditing = () => {
    this.setState({ isEditing: false }, () => {
      if (this.state.results) {
        this.renderCharts(this.state.results)
      }
    })
  }

  /**
   * Map every variant key to a stable colour, ordered by the experiment's own
   * variant list so the split bar, the comparison table and the charts agree.
   * Keys that only appear in results are appended so they still get a colour.
   */
  buildVariantColors = (results) => {
    const keys = (this.state.experiment.get('variants') || [])
      .map(v => v.key)
    if (results && results.variants) {
      Object.keys(results.variants).forEach((key) => {
        if (!keys.includes(key)) {
          keys.push(key)
        }
      })
    }
    const colors = {}
    keys.forEach((key, index) => {
      colors[key] = variantColor(index)
    })
    return colors
  }

  buildLineChart = (canvasId, results, colors, valueFor, formatTick) => {
    const canvas = document.getElementById(canvasId)
    if (!canvas) return null

    return new Chart(canvas.getContext('2d'), {
      type: 'line',
      data: {
        labels: results.dailyData.map(d => d.date),
        datasets: Object.keys(results.variants).map(key => ({
          label: key,
          data: results.dailyData.map(d => valueFor(d.variants[key])),
          borderColor: colors[key],
          backgroundColor: 'transparent',
          pointRadius: 2,
          pointHoverRadius: 4,
          tension: 0.1
        }))
      },
      options: {
        // The card supplies the title and a fixed-height box, so the chart
        // fills its container rather than claiming a page-wide 2:1 block.
        responsive: true,
        maintainAspectRatio: false,
        title: { display: false },
        legend: { position: 'bottom' },
        tooltips: { mode: 'index', intersect: false },
        hover: { mode: 'index', intersect: false },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true, callback: formatTick } }],
          xAxes: [{
            ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 8 }
          }]
        }
      }
    })
  }

  renderCharts = (results) => {
    this.destroyCharts()
    if (!results || !results.dailyData || !results.dailyData.length) {
      this.setState({ chartInstances: [] })
      return
    }

    const colors = this.buildVariantColors(results)
    const asPercent = value => value + '%'
    const asDollars = value => '$' + value

    // Only the primary metric and revenue get a chart. The old page drew three
    // near-identical cumulative curves; they all drift upward as older cohorts
    // age, which reads as a trend when it is only arithmetic.
    const charts = [
      // Both divide by eligible users, matching the comparison table. Dividing
      // by everyone assigned would contradict the numbers directly above them.
      this.buildLineChart(
        'netPaidChart', results, colors,
        v => (v && v.eligible > 0 ? ((v.paying / v.eligible) * 100).toFixed(1) : 0),
        asPercent
      ),
      this.buildLineChart(
        'revenueChart', results, colors,
        v => (v && v.eligible > 0 ? (v.revenue / v.eligible).toFixed(2) : 0),
        asDollars
      )
    ].filter(Boolean)

    this.setState({ chartInstances: charts })
  }

  renderStatus = (status) => {
    const meta = STATUS_META[status]
    if (!meta) {
      return (<span className="badge text-bg-light">{status}</span>)
    }
    return (<span className={'badge ' + meta.badgeClass}>{meta.label}</span>)
  }

  renderLifecycleButtons = () => {
    const status = this.state.experiment.get('status')
    const handleEnd = () => {
      if (window.confirm('Are you sure you want to end this experiment?')) {
        this.handleLifecycleAction('end')
      }
    }

    switch (status) {
      case 'draft':
        return (
          <button
            className="btn btn-success"
            onClick={() => this.handleLifecycleAction('start')}
          >
            Start Experiment
          </button>
        )
      case 'running':
        return (
          <>
            <button
              className="btn btn-warning"
              onClick={() => this.handleLifecycleAction('pause')}
            >
              Pause
            </button>
            <button className="btn btn-dark" onClick={handleEnd}>
              End Experiment
            </button>
          </>
        )
      case 'paused':
        return (
          <>
            <button
              className="btn btn-success"
              onClick={() => this.handleLifecycleAction('start')}
            >
              Resume
            </button>
            <button className="btn btn-dark" onClick={handleEnd}>
              End Experiment
            </button>
          </>
        )
      default:
        return null
    }
  }

  /**
   * Name, status and the lifecycle actions in one band, so the actions are
   * reachable without scrolling past the whole configuration card.
   */
  renderHeader = () => {
    const exp = this.state.experiment
    const status = exp.get('status')
    const isNew = !exp.id

    return (
      <div
        className={'d-flex justify-content-between align-items-start ' +
          'flex-wrap gap-3 mb-4'}
      >
        <div>
          <div className="d-flex align-items-center flex-wrap gap-2">
            <h1 className="h3 mb-0">
              {isNew ? 'New Experiment' : exp.get('name') || 'Experiment'}
            </h1>
            {!isNew && this.renderStatus(status)}
          </div>
          {!isNew && (
            <div className="text-muted small mt-1">
              <code>{exp.get('key')}</code>
              <span className="mx-2">·</span>
              {PLATFORM_LABELS[exp.get('platform')] || 'All platforms'}
              <span className="mx-2">·</span>
              {this.state.assignmentCount.toLocaleString()} assigned
            </div>
          )}
        </div>
        {!isNew && !this.state.isEditing && (
          <div className="d-flex flex-wrap gap-2">
            {status !== 'ended' && (
              <button
                className="btn btn-outline-primary"
                onClick={this.handleStartEditing}
              >
                Edit
              </button>
            )}
            {this.renderLifecycleButtons()}
          </div>
        )}
      </div>
    )
  }

  renderVariantRow = (variant, index, isStarted) => (
    <div key={index} className="input-group mb-2">
      <span className="input-group-text">
        <span
          className="experiment-swatch"
          style={{ backgroundColor: variantColor(index) }}
        />
      </span>
      <input
        type="text"
        className="form-control"
        placeholder="Variant key"
        aria-label="Variant key"
        value={variant.key || ''}
        onChange={(e) =>
          this.handleVariantChange(index, 'key', e.target.value)}
        disabled={isStarted}
      />
      <input
        type="number"
        className="form-control experiment-weight-input"
        placeholder="Weight"
        aria-label="Variant weight"
        value={variant.weight || 0}
        onChange={(e) =>
          this.handleVariantChange(index, 'weight', e.target.value)}
      />
      <span className="input-group-text">%</span>
      <button
        className="btn btn-outline-danger"
        type="button"
        onClick={() => this.handleRemoveVariant(index)}
        disabled={isStarted}
      >
        Remove
      </button>
    </div>
  )

  renderEditForm = () => {
    const exp = this.state.experiment
    const variants = exp.get('variants') || []
    const status = exp.get('status')
    const isStarted = Boolean(status) && status !== 'draft'
    const totalWeight = variants.reduce(
      (sum, v) => sum + (Number(v.weight) || 0), 0
    )

    return (
      <form onSubmit={this.handleSave}>
        <div className="card mb-4">
          <div className="card-header">
            {exp.id ? 'Edit Experiment' : 'New Experiment'}
          </div>
          <div className="card-body">
            {isStarted && (
              <div className="alert alert-warning" role="alert">
                This experiment has already started. Editing will not re-bucket
                users already assigned — they keep their current variant.
                Changes only affect users assigned from now on, which can skew
                results.
              </div>
            )}
            <div className="row g-3">
              <div className="col-md-5">
                <label htmlFor="experimentKey" className="form-label">
                  Key
                </label>
                <input
                  id="experimentKey"
                  type="text"
                  className="form-control"
                  value={exp.get('key') || ''}
                  onChange={(e) =>
                    this.handleFieldChange('key', e.target.value)}
                  placeholder="e.g. iOS-onboarding-test-1"
                  disabled={isStarted}
                  required
                />
                {isStarted && (
                  <div className="form-text">
                    The key cannot be changed once an experiment has started —
                    assignments are linked to it.
                  </div>
                )}
              </div>
              <div className="col-md-4">
                <label htmlFor="experimentName" className="form-label">
                  Name
                </label>
                <input
                  id="experimentName"
                  type="text"
                  className="form-control"
                  value={exp.get('name') || ''}
                  onChange={(e) =>
                    this.handleFieldChange('name', e.target.value)}
                  placeholder="Human-readable name"
                  required
                />
              </div>
              <div className="col-md-3">
                <label htmlFor="experimentPlatform" className="form-label">
                  Platform
                </label>
                <select
                  id="experimentPlatform"
                  className="form-select"
                  value={exp.get('platform') || 'all'}
                  onChange={(e) =>
                    this.handleFieldChange('platform', e.target.value)}
                >
                  <option value="ios">iOS</option>
                  <option value="android">Android</option>
                  <option value="all">All</option>
                </select>
              </div>
              <div className="col-12">
                <label htmlFor="experimentDescription" className="form-label">
                  Description
                </label>
                <textarea
                  id="experimentDescription"
                  className="form-control"
                  value={exp.get('description') || ''}
                  onChange={(e) =>
                    this.handleFieldChange('description', e.target.value)}
                  rows="3"
                  placeholder="What does this experiment test?"
                />
              </div>
            </div>
            <hr className="my-4" />
            <div className="experiment-variants">
              <div
                className={'d-flex justify-content-between ' +
                  'align-items-center mb-2'}
              >
                <span className="form-label mb-0">Variants</span>
                <span
                  className={'badge ' + (totalWeight === 100
                    ? 'text-bg-light'
                    : 'text-bg-warning')}
                >
                  Total weight: {totalWeight}%
                </span>
              </div>
              {variants.map((variant, index) =>
                this.renderVariantRow(variant, index, isStarted))}
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                onClick={this.handleAddVariant}
                disabled={isStarted}
              >
                Add Variant
              </button>
              {isStarted && (
                <div className="form-text">
                  Variants cannot be added, removed, or renamed once an
                  experiment has started — only weights can be changed.
                </div>
              )}
            </div>
          </div>
          <div className="card-footer d-flex gap-2">
            <button type="submit" className="btn btn-primary">Save</button>
            {exp.id && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={this.handleCancelEditing}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </form>
    )
  }

  renderField = (label, value, className) => (
    <div className={className || 'col-sm-4'} key={label}>
      <div className="experiment-label">{label}</div>
      <div>{value}</div>
    </div>
  )

  /**
   * A stacked bar plus a colour legend — the split reads at a glance instead of
   * having to add the weights up from a row of badges.
   */
  renderVariantSplit = () => {
    const variants = this.state.experiment.get('variants') || []
    if (!variants.length) {
      return (<div className="text-muted">No variants configured</div>)
    }

    const total = variants.reduce(
      (sum, v) => sum + (Number(v.weight) || 0), 0
    )

    return (
      <div>
        <div className="experiment-split mb-2">
          {variants.map((variant, index) => (
            <div
              key={variant.key || index}
              className="experiment-split-segment"
              title={`${variant.key}: ${variant.weight}%`}
              style={{
                width: total > 0
                  ? `${((Number(variant.weight) || 0) / total) * 100}%`
                  : '0%',
                backgroundColor: variantColor(index)
              }}
            />
          ))}
        </div>
        <div className="d-flex flex-wrap gap-3">
          {variants.map((variant, index) => (
            <span key={variant.key || index} className="small">
              <span
                className="experiment-swatch me-2"
                style={{ backgroundColor: variantColor(index) }}
              />
              {variant.key}
              <span className="text-muted ms-1">{variant.weight}%</span>
            </span>
          ))}
        </div>
        {total !== 100 && (
          <div className="mt-2">
            <span className="badge text-bg-warning">
              Weights total {total}%, not 100%
            </span>
          </div>
        )}
      </div>
    )
  }

  renderViewMode = () => {
    const exp = this.state.experiment
    const description = exp.get('description')
    const started = formatDate(exp.get('startDate'))
    const ended = formatDate(exp.get('endDate'))
    const empty = (<span className="text-muted">—</span>)

    return (
      <div className="card mb-4">
        <div className="card-header">Configuration</div>
        <div className="card-body">
          <div className="row g-3">
            {this.renderField(
              'Assignments', this.state.assignmentCount.toLocaleString()
            )}
            {this.renderField('Started', started || empty)}
            {this.renderField('Ended', ended || empty)}
          </div>
          <hr className="my-4" />
          <div className="experiment-label mb-2">Traffic Split</div>
          {this.renderVariantSplit()}
          <hr className="my-4" />
          <div className="experiment-label mb-1">Description</div>
          <div className="experiment-description">{description || empty}</div>
        </div>
      </div>
    )
  }

  renderExperimentSummary = () => {
    const { results } = this.state
    if (!results || !results.variants) return null

    const exp = this.state.experiment
    const variantKeys = Object.keys(results.variants)
    const totalUsers = variantKeys.reduce(
      (sum, k) => sum + results.variants[k].userCount, 0
    )
    const totalRevenue = variantKeys.reduce(
      (sum, k) => sum + (results.variants[k].totalRevenue || 0), 0
    )

    const start = toDate(exp.get('startDate'))
    const end = toDate(exp.get('endDate')) || new Date()
    const daysRunning = start
      ? Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)))
      : null

    const totalPaying = variantKeys.reduce(
      (sum, k) => sum + results.variants[k].payingUsers, 0
    )

    // Named the way RevenueCat names them, so a reviewer with the RevenueCat
    // dashboard open in the next tab is reading the same metric under the same
    // word rather than translating between two vocabularies.
    const tiles = [
      { label: 'Customers', value: totalUsers.toLocaleString() },
      { label: 'Paid customers', value: totalPaying.toLocaleString() },
      daysRunning ? { label: 'Days running', value: daysRunning } : null,
      { label: 'Realized LTV', value: formatCurrency(totalRevenue) }
    ].filter(Boolean)

    return (
      <div className="card mb-3">
        <div className="card-body">
          <div className="row text-center">
            {tiles.map((tile, index) => (
              <div
                key={tile.label}
                className={'col-6 col-md' +
                  (index < tiles.length - 1 ? ' border-end' : '')}
              >
                <div className="experiment-stat">{tile.value}</div>
                <div className="experiment-label">{tile.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  /**
   * The lift interval drawn against zero. Whether the interval clears the zero
   * line *is* the decision, and a reviewer should be able to see that without
   * first parsing a p-value.
   */
  renderLiftBar = (liftCI, tone) => {
    if (!liftCI) return null
    const lo = liftCI[0] * 100
    const hi = liftCI[1] * 100
    // A floor on the axis so a tight interval around zero doesn't get blown up
    // to look like a decisive result, and a ceiling so one wild arm doesn't
    // squash everything else into the middle pixel.
    const bound = Math.min(200, Math.max(25, Math.abs(lo), Math.abs(hi)))
    const position = (value) =>
      ((Math.max(-bound, Math.min(bound, value)) + bound) / (2 * bound)) * 100
    const left = position(lo)
    const width = Math.max(position(hi) - left, 1)

    return (
      <div className="mt-2 mb-1">
        <div
          className="experiment-lift-bar"
          title={`95% interval: ${fmtSignedPct(lo)} to ${fmtSignedPct(hi)}`}
        >
          <div
            className={'experiment-lift-range experiment-lift-' + tone}
            style={{ left: `${left}%`, width: `${width}%` }}
          />
          <div className="experiment-lift-zero" />
        </div>
        <div className="experiment-lift-scale">
          <span>Worse</span>
          <span>No change</span>
          <span>Better</span>
        </div>
      </div>
    )
  }

  /**
   * One headline metric for one variant: where it landed, how far that is from
   * control, and how much of that distance is real.
   */
  renderReadout = (options) => {
    const {
      cellKey, title, subtitle, value, baseline, lift, liftCI, verdict
    } = options

    return (
      <div className="col-md-6" key={cellKey}>
        <div className="experiment-label">{title}</div>
        <div className="d-flex align-items-baseline flex-wrap gap-2 mt-1">
          <span className="experiment-stat">{value}</span>
          <span className={'badge ' + verdict.badgeClass}>
            {verdict.label}
          </span>
        </div>
        <div className="text-muted small">
          {baseline} on control &middot; {subtitle}
        </div>
        {lift === null
          ? (
            <div className="text-muted small mt-2">
              No comparison available yet.
            </div>
            )
          : (
            <>
              <div
                className="fw-semibold mt-2"
                style={{ color: TONE_COLORS[verdict.tone] }}
              >
                {fmtSignedPct(lift)} vs control
              </div>
              {this.renderLiftBar(liftCI, verdict.tone)}
              <div className="text-muted small">
                {liftCI
                  ? `True lift is somewhere between ${fmtSignedPct(liftCI[0] * 100)} and ${fmtSignedPct(liftCI[1] * 100)}`
                  : 'Too few conversions to put a range on the lift'}
              </div>
            </>
            )}
      </div>
    )
  }

  /**
   * The answer the reviewer opened the page for, above the table that supports
   * it.
   *
   * These two metrics are the ones these experiments are actually decided on —
   * every experiment in the RevenueCat dashboard sets its primary metric to
   * conversion to paying or realized LTV per customer — so the page leads with
   * them, under RevenueCat's names.
   *
   * The number that settles it is the interval on the *lift*: "somewhere
   * between -9% and +36%" tells a reviewer they cannot act yet, where a rate,
   * an overlapping interval and a p-value leave them to work it out.
   */
  renderHeadlineReadout = () => {
    const validity = this.computeValidity()
    if (!validity) return null

    const { stats, warnings, mde } = validity
    const control = stats.find(s => s.isControl)
    const challengers = stats.filter(s => !s.isControl)
    if (!control || !challengers.length) return null

    const comparisons = this.comparisonCount(stats)
    const threshold = adjustedAlpha(comparisons)
    const colors = this.buildVariantColors(this.state.results)
    const blocking = warnings.filter(w => w.level === 'danger')
    // A blocking check means the arms were never comparable, so every badge
    // below reads "No verdict" rather than contradicting the banner directly
    // above it with a green "Beats control".
    const blocked = blocking.length > 0

    const verdictFor = (metric) => readVerdict({
      pValue: metric.sig ? metric.sig.pValue : null,
      lift: metric.lift,
      threshold,
      blocked
    })

    const readouts = challengers.map(s => ({
      stat: s,
      paying: verdictFor(s.netPaid),
      ltv: verdictFor(s.revenue)
    }))

    const settled = readouts.some(r =>
      r.paying.key === 'win' || r.paying.key === 'loss' ||
      r.ltv.key === 'win' || r.ltv.key === 'loss'
    )

    return (
      <div className="card mb-3">
        <div
          className={'card-header d-flex justify-content-between ' +
            'align-items-center flex-wrap gap-2'}
        >
          <span>Where this lands</span>
          <span className="text-muted small">
            measured against <code>{control.key}</code>
          </span>
        </div>
        <div className="card-body">
          {blocked && (
            <div className="alert alert-danger py-2">
              <small>
                <strong>{blocking[0].title}.</strong> Nothing below is a
                verdict until that is explained — the lifts are shown as
                measured, but they compare arms that were not drawn from the
                same population.
              </small>
            </div>
          )}
          {readouts.map(({ stat, paying, ltv }, index) => (
            <div
              key={stat.key}
              className={index > 0 ? 'border-top pt-4 mt-4' : ''}
            >
              <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                <span
                  className="experiment-swatch"
                  style={{ backgroundColor: colors[stat.key] }}
                />
                <span className="fw-semibold">{stat.key}</span>
                <span className="text-muted small">
                  {stat.eligible.toLocaleString()} eligible customers
                </span>
              </div>
              <div className="row g-4">
                {this.renderReadout({
                  cellKey: 'paying',
                  title: 'Conversion to paying',
                  subtitle: 'paid customers \u00f7 eligible customers',
                  value: `${(stat.netPaid.rate * 100).toFixed(1)}%`,
                  baseline: `${(control.netPaid.rate * 100).toFixed(1)}%`,
                  lift: stat.netPaid.lift,
                  liftCI: stat.netPaid.liftCI,
                  verdict: paying
                })}
                {this.renderReadout({
                  cellKey: 'ltv',
                  title: 'Realized LTV per customer',
                  subtitle: 'revenue earned per eligible customer',
                  value: formatCurrency(stat.revenue.perUser),
                  baseline: formatCurrency(control.revenue.perUser),
                  lift: stat.revenue.lift,
                  liftCI: stat.revenue.liftCI,
                  verdict: ltv
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="card-footer text-muted small">
          {!blocked && !settled && mde !== null && (
            <p className="mb-1">
              Nothing here separates from control yet. At this sample size the
              test can only resolve a lift of{' '}
              <strong>{(mde * 100).toFixed(0)}%</strong> or more on conversion
              to paying — a real effect smaller than that will keep reading as
              &ldquo;too close to call&rdquo; however often you refresh.
            </p>
          )}
          <p className="mb-0">
            A verdict needs p&lt;{threshold.toFixed(4)} — 0.05 divided across
            the {comparisons} comparisons on this page. A win on conversion to
            paying alongside flat realized LTV usually means the variant moved
            swimmers onto a cheaper plan, so check the billing period mix
            before shipping it.
          </p>
        </div>
      </div>
    )
  }

  /**
   * Build every comparison the table needs, from the raw counts the results
   * endpoint sends. Nothing here reconstructs a count from a rate — that is
   * what used to give trial conversion the wrong denominator.
   */
  computeVariantStats = () => {
    const { results } = this.state
    if (!results || !results.variants) return null

    const variantKeys = Object.keys(results.variants)
    const controlKey = variantKeys.includes('control')
      ? 'control'
      : variantKeys[0]
    const control = results.variants[controlKey]

    return variantKeys.map((key) => {
      const m = results.variants[key]
      const isControl = key === controlKey
      const n = m.userCount
      // Swimmers who were already subscribed when they were bucketed can't
      // convert, so every conversion rate divides by the eligible cohort. They
      // are a pre-treatment group, so removing them raises power and makes the
      // absolute rates readable without touching the relative lift.
      const eligible = m.eligibleUsers
      const controlEligible = control.eligibleUsers

      // One proportion metric: rate, interval, and — for non-control variants —
      // lift and a two-proportion test against the same metric on control.
      const metric = (successes, total, controlSuccesses, controlTotal) => {
        const rate = total > 0 ? successes / total : 0
        const ci = wilsonCI(successes, total)
        if (isControl || !controlTotal) {
          return {
            successes, total, rate, ci, lift: null, liftCI: null, sig: null
          }
        }
        const controlRate = controlSuccesses / controlTotal
        const lift = controlRate > 0
          ? ((rate - controlRate) / controlRate) * 100
          : null
        const sig = proportionZTest(
          controlSuccesses, controlTotal, successes, total
        )
        // An interval on the lift, not just on each rate. Two overlapping rate
        // intervals are the classic way to misread a comparison — whether the
        // lift interval clears zero is the question being asked.
        const liftCI = relativeLiftCI(
          controlSuccesses, controlTotal, successes, total
        )
        return { successes, total, rate, ci, lift, liftCI, sig }
      }

      const revenueSummary = {
        count: eligible,
        sum: m.totalRevenue,
        sumOfSquares: m.revenueSumOfSquares
      }
      const controlRevenueSummary = {
        count: controlEligible,
        sum: control.totalRevenue,
        sumOfSquares: control.revenueSumOfSquares
      }
      const revenueLift = control.revenuePerUser > 0
        ? ((m.revenuePerUser - control.revenuePerUser) /
            control.revenuePerUser) * 100
        : null

      return {
        key,
        isControl,
        m,
        n,
        eligible,
        trialStart: metric(
          m.startedTrial, eligible, control.startedTrial, controlEligible
        ),
        directPaid: metric(
          m.directPaid, eligible, control.directPaid, controlEligible
        ),
        started: metric(
          m.everSubscribed, eligible, control.everSubscribed, controlEligible
        ),
        // Denominator is resolved trials, not started trials — a trial still
        // inside its window is neither a win nor a loss.
        trialConversion: metric(
          m.trialConverted, m.resolvedTrials,
          control.trialConverted, control.resolvedTrials
        ),
        netPaid: metric(
          m.payingUsers, eligible, control.payingUsers, controlEligible
        ),
        active: metric(
          m.activeSubscriptions, n,
          control.activeSubscriptions, control.userCount
        ),
        revenue: {
          perUser: m.revenuePerUser,
          ci: meanCI(revenueSummary),
          lift: isControl ? null : revenueLift,
          liftCI: isControl
            ? null
            : meanLiftCI(controlRevenueSummary, revenueSummary),
          sig: isControl
            ? null
            : welchTTest(controlRevenueSummary, revenueSummary)
        }
      }
    })
  }

  /**
   * How many hypothesis tests the table is actually showing. Counting the
   * tests that rendered — rather than assuming a fixed number of metrics —
   * keeps the Bonferroni threshold honest when a metric has no data to test
   * (no resolved trials, say) and stops it drifting as rows are added.
   */
  comparisonCount = (stats) => {
    const tested = stats.reduce((count, s) => {
      if (s.isControl) return count
      const metrics = [
        s.trialStart, s.directPaid, s.started,
        s.trialConversion, s.netPaid, s.active
      ]
      const shown = metrics.filter(m => m.sig && m.total > 0).length
      return count + shown + (s.revenue.sig ? 1 : 0)
    }, 0)
    return Math.max(1, tested)
  }

  /**
   * One metric cell: the rate, its interval, the raw counts behind it, and —
   * for non-control variants — lift against control.
   *
   * Lift is only coloured once it clears the adjusted threshold this page is
   * reading at, which is the same test the headline card applies. A green
   * "+18%" on a result indistinguishable from noise is the single easiest way
   * to talk yourself into shipping a losing variant.
   */
  renderRateCell = (key, metric, options = {}) => {
    const { rate, ci, lift, sig, successes, total } = metric
    const fmt = fmtPctCI(rate, ci)
    const sigInfo = sig ? significanceLabel(sig.pValue) : null
    const verdict = readVerdict({
      pValue: sig ? sig.pValue : null,
      lift,
      threshold: options.threshold
    })

    return (
      <td key={key} className="text-end">
        <span className={options.primary ? 'fw-bold' : 'fw-semibold'}>
          {total > 0 ? `${fmt.pct}%` : '—'}
        </span>
        {lift !== null && total > 0 && (
          <span className="ms-2" style={{ color: TONE_COLORS[verdict.tone] }}>
            {fmtSignedPct(lift)}
            {sigInfo && sigInfo.symbol && (
              <span
                className="ms-1 fw-bold"
                title={`${sigInfo.text} (p=${fmtP(sig.pValue)})`}
              >
                {sigInfo.symbol}
              </span>
            )}
          </span>
        )}
        <div className="text-muted small">
          {total > 0
            ? `${successes.toLocaleString()} / ${total.toLocaleString()}`
            : 'no data'}
        </div>
        {total > 0 && (
          <div className="text-muted small">
            {fmt.lo}% – {fmt.hi}%
            {sig && (
              <span className="ms-2" title={`z=${sig.zScore.toFixed(3)}`}>
                p={fmtP(sig.pValue)}
              </span>
            )}
          </div>
        )}
      </td>
    )
  }

  renderMetricGroup = (label, columnCount, hint) => (
    <tr className="table-light">
      <td colSpan={columnCount + 1}>
        <span className="experiment-label">{label}</span>
        {hint && (
          <span className="text-muted small ms-2 fw-normal">{hint}</span>
        )}
      </td>
    </tr>
  )

  /**
   * Everything that decides whether the comparison table is worth reading at
   * all. A sample ratio mismatch means randomisation is broken; thin coverage
   * means every rate is deflated; short exposure means the trial metrics can't
   * have resolved yet. Each of these silently invalidates a "winner".
   *
   * Computed apart from its panel because the headline card needs it too: a
   * verdict rendered over a broken experiment is worse than no verdict.
   */
  computeValidity = () => {
    const { results } = this.state
    if (!results || !results.variants) return null

    const stats = this.computeVariantStats()
    const srm = sampleRatioMismatch(results.allocation)
    const trialDays = results.trialResolutionDays || 10

    const control = stats.find(s => s.isControl)
    // Net paid conversion divides by eligible users, so the sample size behind
    // it must too — using assigned counts here overstates power and makes the
    // detectable lift look smaller than it is.
    const smallestArm = Math.min(...stats.map(s => s.eligible))
    const mde = control
      ? minimumDetectableEffect(control.netPaid.rate, smallestArm)
      : null

    const totalUsers = stats.reduce((sum, s) => sum + s.n, 0)
    const totalKnown = stats.reduce(
      (sum, s) => sum + s.m.knownSubscriptionState, 0
    )
    const coverage = totalUsers > 0 ? totalKnown / totalUsers : 0
    const coverageIsThin = coverage < 0.9

    const warnings = []
    if (srm && !srm.healthy) {
      warnings.push({
        key: 'srm',
        level: 'danger',
        title: 'Sample ratio mismatch',
        body: 'The observed split does not match the configured weights ' +
          `(chi-square ${srm.chiSquare.toFixed(1)}, ${srm.df} df, ` +
          `p=${fmtP(srm.pValue)}). Either assignment is broken or the weights ` +
          'were changed mid-flight — assignments are sticky, so an edit ' +
          're-splits only new customers. Until this is explained, nothing ' +
          'below is trustworthy.'
      })
    }
    if (coverageIsThin) {
      warnings.push({
        key: 'coverage',
        level: 'warning',
        title: 'Incomplete subscription data',
        body: 'Subscription status is known for ' +
          `${(coverage * 100).toFixed(0)}% of assigned customers. The rest ` +
          'sit in ' +
          'every denominator as non-converters, so every rate below is ' +
          'understated. Refresh again to drain the backfill queue.'
      })
    }
    // Subscription status at assignment is pre-treatment, so randomisation
    // should balance it across arms. If it hasn't, either the eligibility gate
    // depends on the variant or coverage differs between arms — and the
    // filtered comparison inherits that problem.
    const controlPre = control
      ? { count: control.m.preExistingSubscribers, n: control.n }
      : null
    const imbalanced = controlPre
      ? stats.filter((s) => {
        if (s.isControl || s.n === 0 || controlPre.n === 0) return false
        const test = proportionZTest(
          controlPre.count, controlPre.n, s.m.preExistingSubscribers, s.n
        )
        return test.pValue < 0.01
      })
      : []

    if (imbalanced.length > 0) {
      warnings.push({
        key: 'pre-existing-balance',
        level: 'warning',
        title: 'Pre-existing subscribers are not evenly split',
        body: `${imbalanced.map(s => s.key).join(', ')} ` +
          `${imbalanced.length === 1 ? 'has' : 'have'} a materially different ` +
          'share of already-subscribed customers than control. That group ' +
          'is ' +
          'excluded from every conversion rate, so an uneven split means the ' +
          'arms are being compared on different populations. Check whether ' +
          'assignment is gated on subscription status.'
      })
    }

    if (results.eventHistoryFailures > 0) {
      warnings.push({
        key: 'history-failures',
        level: 'warning',
        title: 'RevenueCat event history is failing',
        body: 'Event history is configured but returned nothing for ' +
          `${results.eventHistoryFailures} customer(s) this refresh, so ` +
          'they fell ' +
          'back to the subscriber snapshot — which cannot see a trial that ' +
          'already converted. Check REVENUECAT_V2_API_KEY and ' +
          'REVENUECAT_PROJECT_ID; trial metrics are understated until it is ' +
          'fixed.'
      })
    }
    if (results.eventHistoryAvailable === false) {
      warnings.push({
        key: 'history',
        level: 'warning',
        title: 'Partial trial history',
        body: 'RevenueCat event history is not configured, so backfilled ' +
          'customers fall back to the subscriber snapshot — which cannot ' +
          'see a trial that already converted. Trial metrics understate ' +
          'conversion for customers whose purchases predate experiment ' +
          'tracking.'
      })
    }

    return {
      stats, srm, trialDays, control, mde, totalUsers, coverage, warnings
    }
  }

  /**
   * Whether the comparison can be read at all — in a single line when it can.
   * The per-variant checks matter enormously when something is wrong and are
   * noise when nothing is, so a clean experiment folds them behind a toggle
   * instead of spending half a screen of a reviewer's attention proving a
   * negative.
   */
  renderValidityPanel = () => {
    const validity = this.computeValidity()
    if (!validity) return null
    const { results } = this.state
    const { stats, trialDays, mde, totalUsers, coverage, warnings } = validity

    return (
      <div className="card mb-3">
        <div className="card-header">Can these numbers be trusted?</div>
        <div className="card-body">
          {warnings.length === 0 && (
            <div className="alert alert-success py-2 mb-3">
              <small>
                Nothing here invalidates the comparison: the split matches its
                configuration and subscription data covers{' '}
                {(coverage * 100).toFixed(0)}% of assigned customers.
              </small>
            </div>
          )}
          {warnings.map(warning => (
            <div
              key={warning.key}
              className={`alert alert-${warning.level} py-2 mb-3`}
            >
              <strong className="d-block">{warning.title}</strong>
              <small>{warning.body}</small>
            </div>
          ))}

          <details open={warnings.length > 0}>
            <summary className="experiment-toggle">
              Per-variant checks and how much this test can detect
            </summary>
            <div className="table-responsive mt-3">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '24%' }}>Check</th>
                    {stats.map(s => (
                      <th key={s.key} className="text-end">{s.key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="experiment-metric">
                      Traffic split
                      <div className="text-muted small text-wrap">
                        observed vs configured
                      </div>
                    </td>
                    {stats.map((s) => {
                      const row = (results.allocation || [])
                        .find(a => a.variant === s.key)
                      const observedShare = totalUsers > 0
                        ? (s.n / totalUsers) * 100
                        : 0
                      return (
                        <td key={s.key} className="text-end">
                          <span className="fw-semibold">
                            {observedShare.toFixed(1)}%
                          </span>
                          <div className="text-muted small">
                            configured {row ? row.configuredWeight : '—'}%
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                  <tr>
                    <td className="experiment-metric">
                      Excluded as already subscribed
                      <div className="text-muted small text-wrap">
                        should be evenly split
                      </div>
                    </td>
                    {stats.map((s) => {
                      const pct = s.n > 0
                        ? (s.m.preExistingSubscribers / s.n) * 100
                        : 0
                      return (
                        <td key={s.key} className="text-end">
                          <span className="fw-semibold">{pct.toFixed(1)}%</span>
                          <div className="text-muted small">
                            {s.m.preExistingSubscribers.toLocaleString()} of{' '}
                            {s.n.toLocaleString()}
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                  <tr>
                    <td className="experiment-metric">
                      Subscription data coverage
                    </td>
                    {stats.map((s) => {
                      const pct = s.n > 0
                        ? (s.m.knownSubscriptionState / s.n) * 100
                        : 0
                      return (
                        <td key={s.key} className="text-end">
                          <span
                            className={pct < 90 ? 'fw-semibold text-danger' : 'fw-semibold'}
                          >
                            {pct.toFixed(0)}%
                          </span>
                          <div className="text-muted small">
                            {s.m.knownSubscriptionState.toLocaleString()} /{' '}
                            {s.n.toLocaleString()}
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                  <tr>
                    <td className="experiment-metric">
                      Median exposure
                      <div className="text-muted small text-wrap">
                        time each customer has had to convert
                      </div>
                    </td>
                    {stats.map(s => (
                      <td key={s.key} className="text-end">
                        <span className="fw-semibold">
                          {s.m.medianExposureDays.toFixed(1)} days
                        </span>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="experiment-metric">
                      Trials still in flight
                      <div className="text-muted small text-wrap">
                        excluded from the trial conversion rate
                      </div>
                    </td>
                    {stats.map(s => (
                      <td key={s.key} className="text-end">
                        <span className="fw-semibold">
                          {s.m.usersWithUnresolvedTrialWindow.toLocaleString()}
                        </span>
                        <div className="text-muted small">
                          of {s.m.startedTrial.toLocaleString()} started
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            <hr className="my-3" />
            <div className="row g-3">
              <div className="col-md-6">
                <div className="experiment-label">
                  Smallest detectable lift
                </div>
                <div>
                  {mde !== null
                    ? (
                      <>
                        <span className="experiment-stat">
                          {(mde * 100).toFixed(0)}%
                        </span>
                        <span className="text-muted small ms-2">
                          relative, on conversion to paying, at 80% power
                        </span>
                      </>
                      )
                    : (
                      <span className="text-muted">
                        Not enough data to estimate
                      </span>
                      )}
                </div>
                <div className="text-muted small mt-1">
                  A smaller true effect than this will read as &ldquo;not
                  significant&rdquo; no matter how many times you refresh.
                </div>
              </div>
              <div className="col-md-6">
                <div className="experiment-label">Trial resolution window</div>
                <div>
                  <span className="experiment-stat">{trialDays} days</span>
                </div>
                <div className="text-muted small mt-1">
                  A trial counts toward the trial conversion rate once it
                  converts, lapses, or has been open this long.
                </div>
              </div>
            </div>
          </details>
        </div>
      </div>
    )
  }

  renderCountCell = (key, value, subtitle) => (
    <td key={key} className="text-end">
      <span className="fw-semibold">{value}</span>
      {subtitle && <div className="text-muted small">{subtitle}</div>}
    </td>
  )

  /**
   * Product mix of first purchases. Free trials only exist on some products, so
   * a variant can move the headline conversion rate purely by shifting swimmers
   * between yearly and monthly — this is the row that tells the two apart.
   */
  renderProductMixCell = (stat) => {
    const mix = stat.m.productMix || {}
    const total = Object.values(mix).reduce((sum, v) => sum + v, 0)
    if (total === 0) {
      return (
        <td key={stat.key} className="text-end text-muted small">
          No purchases yet
        </td>
      )
    }

    const share = (count) => ((count / total) * 100).toFixed(0)
    const rows = [
      ['Yearly', mix.yearly],
      ['Quarterly', mix.quarterly],
      ['Monthly', mix.monthly],
      ['Lifetime', mix.lifetime]
    ].filter(([, count]) => count > 0)

    return (
      <td key={stat.key} className="text-end">
        {rows.map(([label, count]) => (
          <div key={label} className="small">
            {label} <span className="fw-semibold">{share(count)}%</span>
            <span className="text-muted ms-1">({count})</span>
          </div>
        ))}
        {mix.unclassified > 0 && (
          <div
            className="small text-muted"
            title={'Store SKUs whose billing period could not be read — ' +
              'mostly Stripe. These purchases are still counted in the mix ' +
              'total; the bucket is shown so the yearly/monthly split is not ' +
              'mistaken for the whole picture.'}
          >
            Unclassified {share(mix.unclassified)}% ({mix.unclassified})
          </div>
        )}
      </td>
    )
  }

  renderRevenueCell = (stat, threshold) => {
    const { perUser, ci, lift, sig } = stat.revenue
    const sigInfo = sig ? significanceLabel(sig.pValue) : null
    const isSignificant = Boolean(sigInfo && sigInfo.symbol)
    const verdict = readVerdict({
      pValue: sig ? sig.pValue : null,
      lift,
      threshold
    })

    return (
      <td key={stat.key} className="text-end">
        <span className="fw-semibold">{formatCurrency(perUser)}</span>
        {lift !== null && (
          <span className="ms-2" style={{ color: TONE_COLORS[verdict.tone] }}>
            {fmtSignedPct(lift)}
            {isSignificant && (
              <span
                className="ms-1 fw-bold"
                title={`${sigInfo.text} (p=${fmtP(sig.pValue)})`}
              >
                {sigInfo.symbol}
              </span>
            )}
          </span>
        )}
        <div className="text-muted small">
          {formatCurrency(ci[0])} – {formatCurrency(ci[1])}
          {sig && (
            <span className="ms-2" title={`t=${sig.tStat.toFixed(3)}`}>
              p={fmtP(sig.pValue)}
            </span>
          )}
        </div>
      </td>
    )
  }

  renderResultsTable = () => {
    const stats = this.computeVariantStats()
    if (!stats) return null

    const colors = this.buildVariantColors(this.state.results)
    const colWidth = `${76 / stats.length}%`
    const comparisons = this.comparisonCount(stats)
    const threshold = adjustedAlpha(comparisons)
    const trialDays = this.state.results.trialResolutionDays || 10

    return (
      <div className="card mb-3">
        <div
          className={'card-header d-flex justify-content-between ' +
            'align-items-center flex-wrap gap-2'}
        >
          <span>Full comparison</span>
          <span className="text-muted small">
            rate &middot; lift vs control &middot; counts &middot; 95% interval
            and p
          </span>
        </div>
        <div className="table-responsive">
          <table
            className={'table table-sm table-striped table-hover align-middle ' +
              'experiment-results-table mb-0'}
          >
            <thead>
              <tr>
                <th style={{ width: '24%' }}>Metric</th>
                {stats.map(s => (
                  <th
                    key={s.key}
                    className="text-end"
                    style={{ width: colWidth }}
                  >
                    <span
                      className="experiment-swatch me-2"
                      style={{ backgroundColor: colors[s.key] }}
                    />
                    {s.key}
                    {s.isControl && (
                      <span className="badge text-bg-secondary ms-2">
                        Control
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {this.renderMetricGroup(
                'Audience', stats.length, 'who is in each arm'
              )}
              <tr>
                <td className="experiment-metric">Customers</td>
                {stats.map(s => this.renderCountCell(
                  s.key, s.n.toLocaleString()
                ))}
              </tr>
              <tr>
                <td className="experiment-metric">
                  Already subscribed at assignment
                  <div className="text-muted small text-wrap">
                    Excluded from conversion rates
                  </div>
                </td>
                {stats.map(s => this.renderCountCell(
                  s.key,
                  s.m.preExistingSubscribers.toLocaleString(),
                  s.n > 0
                    ? `${((s.m.preExistingSubscribers / s.n) * 100).toFixed(1)}% of assigned`
                    : null
                ))}
              </tr>
              <tr>
                <td className="experiment-metric">
                  Eligible customers
                  <div className="text-muted small text-wrap">
                    Denominator for every conversion rate below
                  </div>
                </td>
                {stats.map(s => this.renderCountCell(
                  s.key, s.eligible.toLocaleString()
                ))}
              </tr>

              {this.renderMetricGroup(
                'Initial conversion', stats.length,
                'did the variant start more subscriptions?'
              )}
              <tr>
                <td className="experiment-metric">
                  Initial conversion rate
                  <div className="text-muted small text-wrap">
                    Started a trial or paid outright
                  </div>
                </td>
                {stats.map(s => this.renderRateCell(
                  s.key, s.started, { threshold }
                ))}
              </tr>
              <tr>
                <td className="experiment-metric">
                  Trial start rate
                  <div className="text-muted small text-wrap">
                    Of those, the ones who took a free trial
                  </div>
                </td>
                {stats.map(s => this.renderRateCell(
                  s.key, s.trialStart, { threshold }
                ))}
              </tr>
              <tr>
                <td className="experiment-metric">
                  Direct purchase rate
                  <div className="text-muted small text-wrap">
                    Of those, the ones who paid without a trial
                  </div>
                </td>
                {stats.map(s => this.renderRateCell(
                  s.key, s.directPaid, { threshold }
                ))}
              </tr>
              <tr>
                <td className="experiment-metric">
                  Trial conversion rate
                  <div className="text-muted small text-wrap">
                    Trials converted &divide; trials that have resolved
                  </div>
                </td>
                {stats.map(s => this.renderRateCell(
                  s.key, s.trialConversion, { threshold }
                ))}
              </tr>

              {this.renderMetricGroup(
                'Paid customers', stats.length,
                'did those subscriptions turn into money?'
              )}
              <tr className="table-warning">
                <td className="experiment-metric fw-bold text-dark">
                  Conversion to paying
                  <span className="badge text-bg-dark ms-2">Primary</span>
                  <div className="text-muted small text-wrap fw-normal">
                    Paid customers &divide; eligible customers
                  </div>
                </td>
                {stats.map(s => this.renderRateCell(
                  s.key, s.netPaid, { primary: true, threshold }
                ))}
              </tr>
              <tr>
                <td className="experiment-metric">
                  Active subscriber rate
                  <span className="badge text-bg-light ms-2">Guardrail</span>
                  <div className="text-muted small text-wrap">
                    Over all assigned customers, including pre-existing
                    subscribers
                  </div>
                </td>
                {stats.map(s => this.renderRateCell(
                  s.key, s.active, { threshold }
                ))}
              </tr>

              {this.renderMetricGroup(
                'Billing period of first purchase', stats.length,
                'or did it just move swimmers between plans?'
              )}
              <tr>
                <td className="experiment-metric">Product mix</td>
                {stats.map(s => this.renderProductMixCell(s))}
              </tr>

              {this.renderMetricGroup(
                'Revenue', stats.length, 'what it was worth'
              )}
              <tr>
                <td className="experiment-metric">
                  Realized LTV per customer
                  <div className="text-muted small text-wrap">
                    Per eligible customer; excludes renewals from pre-existing
                    subscribers
                  </div>
                </td>
                {stats.map(s => this.renderRevenueCell(s, threshold))}
              </tr>
              <tr>
                <td className="experiment-metric">
                  Realized LTV per paying customer
                  <div className="text-muted small text-wrap">
                    Mix indicator, not a decision metric
                  </div>
                </td>
                {stats.map(s => this.renderCountCell(
                  s.key,
                  formatCurrency(s.m.revenuePerPayingUser),
                  `${formatCurrency(s.m.totalRevenue)} total`
                ))}
              </tr>
              <tr>
                <td className="experiment-metric">
                  Pre-existing subscriber revenue
                  <div className="text-muted small text-wrap">
                    Renewals the experiment did not cause — not in the figures
                    above
                  </div>
                </td>
                {stats.map(s => this.renderCountCell(
                  s.key, formatCurrency(s.m.preExistingRevenue)
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="card-footer text-muted small">
          <p className="mb-1">
            Metric names match RevenueCat&rsquo;s, so this table and the
            RevenueCat dashboard can be read side by side.
          </p>
          <p className="mb-1">
            Lift is measured against{' '}
            <strong>{stats.find(s => s.isControl)?.key}</strong> and is only
            coloured once it clears p&lt;{threshold.toFixed(4)} — the
            Bonferroni-adjusted level, 0.05 spread across the{' '}
            <strong>{comparisons}</strong> comparisons this table runs at once. A metric can therefore carry a <strong>*</strong>{' '}
            (p&lt;0.05) or <strong>**</strong> (p&lt;0.01) and still be grey:
            significant on its own, not once the whole table is accounted for.
            Proportion intervals use the Wilson score method; revenue uses a
            Welch t-test on per-customer revenue.
          </p>
          <p className="mb-1">
            Conversion rates divide by <strong>eligible customers</strong> —
            everyone assigned except those already subscribed when they were
            bucketed, who cannot convert. That is a pre-treatment group, so
            excluding them sharpens the estimate without changing the relative
            lift. Active subscriber rate is the exception: it spans everyone
            assigned, because an existing subscriber churning is a real
            outcome.
          </p>
          <p className="mb-1">
            <strong>Trial conversion rate</strong> counts only trials that have
            had {trialDays} days to resolve. A trial still inside its window is
            neither a win nor a loss, so it is excluded rather than counted as
            a failure.
          </p>
          <p className="mb-0">
            Decide on the primary metric; treat the rest as diagnostics. These
            are fixed-horizon tests, so re-running them daily and stopping at
            the first significant result inflates the false positive rate well
            beyond the nominal 5%.
          </p>
        </div>
      </div>
    )
  }

  renderChartCard = (title, canvasId, className) => (
    <div className={className}>
      <div className="card h-100">
        <div className="card-header">{title}</div>
        <div className="card-body">
          <div className="experiment-chart">
            <canvas id={canvasId} />
          </div>
        </div>
      </div>
    </div>
  )

  renderChartGrid = () => {
    const { results } = this.state
    if (!results.dailyData || !results.dailyData.length) {
      return (
        <div className="card">
          <div className="card-body text-center text-muted py-4">
            No daily breakdown is available for this experiment yet.
          </div>
        </div>
      )
    }

    return (
      <>
        <div className="row g-3">
          {this.renderChartCard(
            'Conversion to paying (cumulative)', 'netPaidChart', 'col-xl-6'
          )}
          {this.renderChartCard(
            'Realized LTV per customer (cumulative)', 'revenueChart', 'col-xl-6'
          )}
        </div>
        <div className="text-muted small mt-2">
          Both curves are cumulative over everyone assigned up to each date, so
          they rise as earlier cohorts age. Compare the variants against each
          other on a given day; don&rsquo;t read the slope as a trend.
        </div>
      </>
    )
  }

  renderResults = () => {
    const status = this.state.experiment.get('status')
    if (status === 'draft') return null

    const { results, loadingResults } = this.state

    return (
      <div className="mb-4">
        <div
          className={'d-flex justify-content-between align-items-center ' +
            'flex-wrap gap-2 mb-3'}
        >
          <div>
            <h2 className="h4 mb-0">Results</h2>
            {results && (
              <span className="text-muted small">
                Computed {new Date(results.computedAt).toLocaleString()}
              </span>
            )}
          </div>
          <button
            className="btn btn-outline-primary"
            onClick={this.handleRefreshResults}
            disabled={loadingResults}
          >
            {loadingResults
              ? 'Loading...'
              : results ? 'Refresh Results' : 'Load Results'}
          </button>
        </div>
        {!results && (
          <div className="card">
            <div className="card-body text-center text-muted py-5">
              {loadingResults
                ? 'Crunching the numbers...'
                : 'Results are computed on demand — load them to see how the ' +
                  'variants compare.'}
            </div>
          </div>
        )}
        {results && (
          <>
            {results.pendingBackfill > 0 && (
              <div className="alert alert-info py-2">
                <small>
                  {results.pendingBackfill}{' '}
                  {results.pendingBackfill === 1 ? 'customer' : 'customers'}{' '}
                  still
                  pending subscription backfill. Click{' '}
                  <strong>Refresh Results</strong> again to process the next
                  batch.
                </small>
              </div>
            )}
            {this.renderExperimentSummary()}
            {this.renderHeadlineReadout()}
            {this.renderValidityPanel()}
            {this.renderResultsTable()}
            {this.renderChartGrid()}
          </>
        )}
      </div>
    )
  }

  render = () => {
    if (!this.state.experiment) {
      return (
        <div className="container my-4">
          <div className="text-muted">Loading...</div>
        </div>
      )
    }

    return (
      <div className="container my-4">
        <a
          href="/#/experiments"
          className="small text-decoration-none d-inline-block mb-2"
        >
          &larr; All Experiments
        </a>
        {this.renderHeader()}
        {this.state.isEditing
          ? this.renderEditForm()
          : this.renderViewMode()}
        {!this.state.isEditing && this.renderResults()}
      </div>
    )
  }
}
